import { createContext, useContext, type ReactNode } from "react";

// The context and its hooks live apart from the provider so that Toast.tsx
// exports a component and nothing else — a module that mixes the two cannot be
// hot-replaced, and every screen in the admin sits under this provider.

export type ToastVariant = "info" | "success" | "warn" | "danger";

export interface Toast {
  id: string;
  variant: ToastVariant;
  title?: string;
  message: string;
  /**
   * One control at the toast's right edge, before the ×. The shape `Alert`
   * already has, and for the same reason: an Undo put through `message` lands
   * inside the sentence, and every caller that wanted it beside the text would
   * restate the same flex rules to pull it back out.
   *
   * A toast is the only place an Undo can live — it is the notification that a
   * row left the package, and the offer to put it back has nowhere else to be.
   */
  action?: ReactNode;
}

export interface ToastApi {
  push: (t: Omit<Toast, "id">) => void;
}

export const ToastCtx = createContext<ToastApi | null>(null);

export function useToast() {
  const c = useContext(ToastCtx);
  if (!c) throw new Error("useToast must be inside ToastProvider");
  return c;
}

/**
 * Like {@link useToast} but returns `null` when no provider is mounted instead
 * of throwing. Used by global UI (e.g. NetworkBanner) so it stays usable in
 * unit tests that don't wrap with ToastProvider.
 */
export function useOptionalToast(): ToastApi | null {
  return useContext(ToastCtx);
}
